import React from 'react';
import { CircleCheck, Scale } from 'lucide-react';

import { FORMALIA } from '../data/formaliaSpec';
import Badge from './ui/Badge';
import { cn } from './ui/styles';

// --- Shared Types ---
export interface ValidationResult {
    rule_id: string;
    severity: "FAIL" | "WARN";
    message: string;
    location?: string;
    details?: {
        snippet?: string;
    };
}

export interface FormaliaBadgeProps {
    report?: ValidationResult[];
    article?: {
        title?: string;
        teaser?: string;
        body?: string;
        key_points?: string[];
        faq?: { question: string; answer: string }[];
        meta_title?: string;
    };
}

/** One house-style limit as a tile: the part of the article, its size and the allowed range. */
interface Metric {
    label: string;
    value: number;
    /** The unit, singular and plural. */
    unit: [string, string];
    min: number;
    max: number;
    /** The spec's hard limit, where it has one (teaser); only shown. */
    hard_max?: number;
    required?: boolean;
}

function MetricTile({ label, value, unit, min, max }: Metric) {
    const ok = value >= min && value <= max;
    return (
        <div className={cn('rounded-lg p-2', ok ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')}>
            <div className="font-semibold">{label}</div>
            <div>{value} {value === 1 ? unit[0] : unit[1]}</div>
            <div className="opacity-80">{min === max ? `(${min})` : `(${min}–${max})`}</div>
            {ok && <CircleCheck className="mx-auto mt-1 h-3.5 w-3.5" aria-hidden="true" />}
            <span className="sr-only">{ok ? 'im Zielbereich' : 'außerhalb des Zielbereichs'}</span>
        </div>
    );
}

/** One validator finding: its severity, where it is, what is wrong and the passage. */
function Finding({ finding }: { finding: ValidationResult }) {
    const fail = finding.severity === "FAIL";
    return (
        <li
            className={cn(
                'flex items-start gap-2 rounded-lg border p-2',
                fail ? 'border-danger/30 bg-danger-soft' : 'border-warning/40 bg-warning-soft',
            )}
        >
            <span
                className={cn(
                    'w-12 shrink-0 pt-0.5 text-[10px] font-bold uppercase tracking-wider',
                    fail ? 'text-danger' : 'text-warning',
                )}
            >
                {finding.severity}
            </span>
            <div className="min-w-0 flex-1 text-fg">
                {finding.location && <span className="mb-0.5 block font-semibold">{finding.location}:</span>}
                <span>{finding.message}</span>
                {finding.details?.snippet && (
                    <div
                        className={cn(
                            'mt-2 rounded-md border bg-surface p-2 font-mono text-[11px] wrap-break-word',
                            fail ? 'border-danger/30' : 'border-warning/40',
                        )}
                    >
                        „{finding.details.snippet}“
                    </div>
                )}
            </div>
        </li>
    );
}

/**
 * FormaliaBadge - Shared component for displaying article validation results
 * Used by the Article Generator, FormaliaCheckTool and the other editors.
 *
 * The ranges come from src/app/data/hkcm_formalia_spec.json (data/formaliaSpec.ts),
 * the spec the validator checks against; the FAQ is optional (0 is in range).
 * FAIL and WARN are the validator's severities (glossary: "Severity: FAIL/WARN").
 */
export const FormaliaBadge: React.FC<FormaliaBadgeProps> = ({ report, article }) => {
    // Calculate metrics
    const h1Chars = article?.title?.length ?? 0;
    const teaserChars = article?.teaser?.length ?? 0;
    const bodyWords = article?.body?.split(/\s+/).filter(Boolean).length ?? 0;
    const kpCount = article?.key_points?.length ?? 0;
    const faqCount = article?.faq?.length ?? 0;
    const metaTitleChars = article?.meta_title?.length ?? 0;

    const metrics: Metric[] = [
        { label: 'H1', value: h1Chars, unit: ['Zeichen', 'Zeichen'], ...FORMALIA.h1 },
        { label: 'Teaser', value: teaserChars, unit: ['Zeichen', 'Zeichen'], ...FORMALIA.teaser },
        { label: 'Text', value: bodyWords, unit: ['Wort', 'Wörter'], ...FORMALIA.body },
        { label: 'Kernpunkte', value: kpCount, unit: ['Eintrag', 'Einträge'], ...FORMALIA.keyPoints },
        { label: 'FAQ', value: faqCount, unit: ['Eintrag', 'Einträge'], ...FORMALIA.faq },
        { label: 'Meta-Titel', value: metaTitleChars, unit: ['Zeichen', 'Zeichen'], ...FORMALIA.metaTitle },
    ];

    // Group by severity
    const fails = report?.filter(r => r.severity === "FAIL") ?? [];
    const warns = report?.filter(r => r.severity === "WARN") ?? [];
    const isClean = fails.length === 0 && warns.length === 0;

    return (
        <div className="mb-6 rounded-2xl border border-line bg-surface-subtle p-4 animate-fadeIn">
            <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-fg-strong">
                    <Scale className="h-4 w-4 text-primary" aria-hidden="true" />
                    Formalia Check
                </h3>
                {isClean ? (
                    <Badge tone="success" icon={CircleCheck}>Bestanden</Badge>
                ) : (
                    <div className="flex gap-2">
                        {fails.length > 0 && <Badge tone="danger">{fails.length} FAIL</Badge>}
                        {warns.length > 0 && <Badge tone="warning">{warns.length} WARN</Badge>}
                    </div>
                )}
            </div>

            {/* Metrics Grid */}
            {article && (
                <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
                    {metrics.map((metric) => (
                        <MetricTile key={metric.label} {...metric} />
                    ))}
                </div>
            )}

            {!isClean && (
                <ul className="max-h-60 space-y-2 overflow-y-auto pr-1 text-xs">
                    {[...fails, ...warns].map((finding, i) => (
                        <Finding key={`${finding.severity}-${i}`} finding={finding} />
                    ))}
                </ul>
            )}
            {isClean && (
                <p className="flex items-center gap-2 p-2 text-xs italic text-success">
                    <CircleCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Alle Vorgaben zu Aufbau, Länge und Formatierung sind erfüllt.
                </p>
            )}
        </div>
    );
};

export default FormaliaBadge;
